import React, { useState, useEffect, useMemo } from 'react';
import useAxiosPrivateSAI from '../../../../hooks/useAxiosPrivateSAI';
import useAuth from '../../../../hooks/useAuth';

const StepOneEdit = ({ data, onUpdate, policyId, errors, mutualGbs, claim = null }) => {
  const axios = useAxiosPrivateSAI();
  const [conditions, setConditions] = useState([]);
  const [filteredConditions, setFilteredConditions] = useState([]);
  const [showPhased, setShowPhased] = useState(false);
  const [showConfidential, setShowConfidential] = useState(false);
  const { setShowLoader } = useAuth();

  useEffect(() => {
    const fetchConditions = async () => {
      try {
        const response = await axios.get(`/api/conditions?ascending=1&pid=${policyId}`)
        const rawConditions = response.data.data

        if (typeof rawConditions !== 'object') {
          console.error('Unexpected format for conditions')
          return
        }

        const conditionArray = Object.values(rawConditions).sort((a, b) =>
          a.condition_title.localeCompare(b.condition_title)
        )

        setConditions(conditionArray)
        setFilteredConditions(conditionArray)
      } catch (err) {
        console.error('Error fetching conditions:', err)
      }
    }

    if (policyId) fetchConditions()
  }, [policyId])

  useEffect(() => {
    if (!claim?.condition || conditions.length === 0) return;

    const matched = conditions.find((c) => c.condition_title === claim.condition);
    if (matched) {
      onUpdate({ selectedCondition: matched })
    }
  }, [conditions, claim]);

  // Update toggles based on selected condition
  useEffect(() => {
    if (data.selectedCondition) {
      if (data.selectedCondition.condition_phased === 1) {
        setShowPhased(true)
      } else {
        setShowPhased(false);
        onUpdate({ ...data, isPhasedReturn: false, isShielding: false, isConfidential: false });
      }

      if (data.selectedCondition.condition_id === 'ed685ef6-7b54-11e7-a963-336a7d328401') {
        setShowConfidential(true);
      } else {
        setShowConfidential(false);
      }
    } else {
      setShowPhased(false);
      setShowConfidential(false);
    }
  }, [data.selectedCondition])

  const allowedStatuses = new Set([
    0, 1, 2, 3, 4, 5, 12, 13, 14, 15, 16, 28, 33, 35,
  ]);

  const isEndDateDisabled = useMemo(() => {
    if (!claim?.claim_id || !claim?.claim_absence_start_date) {
      return true;
    }
    return !allowedStatuses.has(Number(claim?.claim_status_id));
  }, [claim?.claim_id, claim?.claim_absence_start_date, claim?.claim_status_id]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Staff name */}
      <div>
        <label className="block mb-1 font-medium text-sm text-gray-700">
          Staff Name <span className="text-red-600">*</span>
        </label>
        <input
          value={claim?.claim_staff_name || data.selectedStaff?.name || ''}
          disabled
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>

      {/* Condition */}
      <div>
        <label className="block mb-1 font-medium text-sm text-gray-700">
          Condition <span className="text-red-600">*</span>
        </label>
        <select
          value={data.selectedCondition ? JSON.stringify(data.selectedCondition) : ''}
          onChange={(e) =>
            onUpdate({ ...data, selectedCondition: JSON.parse(e.target.value) })
          }
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          <option value="" disabled>
            Select a condition
          </option>
          {filteredConditions.map((cond) => (
            <option key={cond.condition_id} value={JSON.stringify(cond)} disabled={cond.disabled}>
              {cond.condition_title}
            </option>
          ))}
        </select>
      </div>

      {/* Absence start */}
      <div>
        <label className="block mb-1 font-medium text-sm text-gray-700">
          First full day of absence <span className="text-red-600">*</span>
        </label>
        <input
          type="date"
          value={data.absenceStart || ''}
          onChange={(e) => onUpdate({ ...data, absenceStart: e.target.value })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>

      {/* Absence end */}
      <div>
        <label className="block mb-1 font-medium text-sm text-gray-700">
          Last day of absence (only select this date if the staff member has returned)
        </label>
        <input
          type="date"
          value={data.absenceEnd || ''}
          onChange={(e) => onUpdate({ ...data, absenceEnd: e.target.value })}
          // disabled={isEndDateDisabled}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>

      {/* Extra options */}
      {data.selectedStaff && data.selectedCondition && !errors?.invalidCondition && (
        <>
          {!mutualGbs && (
            <fieldset style={{ marginBottom: 0 }}>
              <div className="form-group claim-config-form field-switch" style={{ color: 'red' }}>
                <label htmlFor="claim-shielding">
                  <input
                    type="checkbox"
                    className="checkbox mr-2"
                    id="claim-shielding"
                    checked={data.isShielding}
                    onChange={(e) => onUpdate({ ...data, isShielding: e.target.checked })}
                  />
                  Is staff member shielding?
                </label>
              </div>
            </fieldset>
          )}

          {showConfidential && (
            <fieldset style={{ marginBottom: 0 }}>
              <div className="form-group claim-config-form field-switch" style={{ color: 'grey' }}>
                <label htmlFor="claim-confidential">
                  <input
                    type="checkbox"
                    className="checkbox mr-2"
                    id="claim-confidential"
                    checked={data.isConfidential}
                    onChange={(e) => onUpdate({ ...data, isConfidential: e.target.checked })}
                  />
                  Is this claim confidential?
                </label>
              </div>
            </fieldset>
          )}

          {showPhased && (
            <fieldset style={{ marginBottom: 0 }}>
              <div className="form-group claim-config-form phased-return-switch field-switch">
                <label htmlFor="claim-claim_phased">
                  <input
                    type="checkbox"
                    className="checkbox mr-2"
                    id="claim-claim_phased"
                    checked={data.isPhasedReturn}
                    onChange={(e) =>
                      onUpdate({ ...data, isPhasedReturn: e.target.checked })
                    }
                  />
                  Is this a phased return?
                </label>
              </div>
            </fieldset>
          )}
        </>
      )}

      {/* Error messages */}
      {errors?.missingFields && (
        <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.missingFields}</strong>
        </div>
      )}

      {errors?.incomplete && (
        <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.incomplete}</strong>
        </div>
      )}

      {errors?.startDate && (
        <div className="bg-red-100 border border-red-300 text-red-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.startDate}</strong>
        </div>
      )}

      {errors?.endDate && (
        <div className="bg-red-100 border border-red-300 text-red-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.endDate}</strong>
        </div>
      )}

      {errors?.invalidCondition && (
        <div className="bg-blue-100 border border-blue-300 text-blue-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.invalidCondition}</strong>
          <br />
          Please contact your Account Manager on 01773 814400 or speak to us using our Live Chat.
        </div>
      )}
    </div>
  );
};

export default StepOneEdit;

